import { Typography } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { VisualArticle } from 'components/domain/stockPricing/StockDisplay/VisualArticle'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AppCurrency,
  FINISH_TYPES_CONVERSIONS,
  getAvailableQuantityNetOfPickedAndWithIssues,
  getDisplayLocationName,
  InventoryArticle,
  Location,
  OrderArticle,
} from 'shared'

export interface LocationsDialogInterface {
  orderArticle: OrderArticle
  quantity: number
  open: boolean
  setOpen: (open: boolean) => void
  handleChoosePickingLocation: (order: OrderArticle) => void
  readonly: boolean
  handleReportIssue: (order: OrderArticle) => void
  pickedArticles: Map<number, OrderArticle[]>
}

const getInventoryArticleFromOrderArticle = (orderArticle: OrderArticle) => {
  const iA = new InventoryArticle({
    attributes: [...orderArticle.attributes!],

    isSigned: orderArticle.isSigned,
    isAltered: orderArticle.isAltered,
    isPlayset: orderArticle.isPlayset,
    isFirstEd: orderArticle.isFirstEd,
    finishType: FINISH_TYPES_CONVERSIONS.find((entry) => entry.label === orderArticle.finishType)!
      .label,
    countryEdition: orderArticle.countryEdition,
    grade: orderArticle.grade,
    gradingCompany: orderArticle.gradingCompany,

    locations: orderArticle.locations,
    ptProductId: orderArticle.ptArticleId,

    card: orderArticle.card,
  })

  return iA
}

const getMapOfLocationNameAndDeltaQ = (pickedArticles: OrderArticle[]) => {
  const map = new Map<string, number>()
  if (pickedArticles && pickedArticles.length > 0)
    pickedArticles.forEach((article) => {
      if (!article.locationInfo) return
      map.set(article.locationInfo.name, article.locationInfo.deltaQuantity!)
    })

  return map
}

const getDeltaQ = (
  readonly: boolean,
  locationName: string,
  listOfPickedLocations: Map<string, number>
) => {
  if (!readonly) return 0
  return listOfPickedLocations.get(locationName) ?? 0
}

export const LocationsDialog = ({
  orderArticle,
  quantity,
  open,
  setOpen,
  handleChoosePickingLocation,
  readonly,
  handleReportIssue,
  pickedArticles,
}: LocationsDialogInterface): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useUser()
  const showEmptyLocations = user.showEmptyPickingLocations ?? false
  const [pickedCards, setPickedCards] = useState(0)
  const [initialLocations, setInitialLocations] = useState<Location[]>([])
  const [locationsObj, setLocationsObj] = useState<Location[]>([])

  const listOfPickedLocations = getMapOfLocationNameAndDeltaQ(
    pickedArticles.get(orderArticle.ptOrderArticleId!)!
  )

  const currentArticle = getInventoryArticleFromOrderArticle(orderArticle)

  currentArticle!.quantity = orderArticle!.locations?.reduce(
    (acc, loc) => acc + getAvailableQuantityNetOfPickedAndWithIssues(loc),
    0
  )

  useEffect(() => {
    if (orderArticle) {
      const initialLocationsObj = orderArticle.locations
        .filter((location) => {
          if (location.name.toLowerCase().startsWith('unknown')) {
            const nameParts = location.name.split('-')
            if (nameParts.length === 2 && nameParts[1] === orderArticle.ptOrderId.toString()) {
              return true
            }
            return false
          }
          return true
        })
        .map((location) => ({
          ...location,
          deltaQuantity: getDeltaQ(readonly, location.name, listOfPickedLocations) ?? 0,
          totalPickedQuantity: location.totalPickedQuantity ?? 0,
        }))
      const initialLocs = orderArticle.locations
        .filter((location) => {
          if (location.name.toLowerCase().startsWith('unknown')) {
            const nameParts = location.name.split('-')
            if (nameParts.length === 2 && nameParts[1] === orderArticle.ptOrderId.toString()) {
              return true
            }
            return false
          }
          return true
        })
        .map((location) => ({
          ...location,
          quantity: location.quantity,
          deltaQuantity: getDeltaQ(readonly, location.name, listOfPickedLocations),
          totalPickedQuantity: location.totalPickedQuantity ?? 0,
        }))

      initialLocationsObj.sort((a, b) => {
        if (a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase()) {
          return -1
        }
        if (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase()) {
          return 1
        }
        return 0
      })

      initialLocs.sort((a, b) => {
        if (a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase()) {
          return -1
        }
        if (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase()) {
          return 1
        }
        return 0
      })
      if (!readonly) initialLocationsObj.forEach((loc) => (loc.deltaQuantity! = 0))

      setInitialLocations(initialLocs)
      setLocationsObj(initialLocationsObj)
      setPickedCards(orderArticle.pickedQuantity)
    }
  }, [orderArticle])

  const incrementPickingQuantity = (name: string) => {
    setLocationsObj((prevLocations) => {
      const locationToUpdate = prevLocations.find((location) => location.name === name)
      const startingValues = initialLocations.find((location) => location.name === name)

      if (!locationToUpdate) return prevLocations

      const newDeltaQuantity = Math.abs(locationToUpdate.deltaQuantity ?? 0) + 1
      const newReservedQuantity = (locationToUpdate.totalPickedQuantity ?? 0) + 1

      if (
        newDeltaQuantity <=
          locationToUpdate.quantity -
            ((startingValues!.totalPickedQuantity ?? 0) +
              (startingValues!.totalQuantityWithIssues ?? 0)) &&
        pickedCards < quantity
      ) {
        setPickedCards((prevPickedCards) => prevPickedCards + 1)

        return prevLocations.map((location) =>
          location.name === name
            ? {
                ...location,
                deltaQuantity: -newDeltaQuantity,
                totalPickedQuantity: newReservedQuantity,
              }
            : location
        )
      }

      return prevLocations
    })
  }

  const decrementPickingQuantity = (name: string) => {
    setLocationsObj((prevLocations) => {
      const locationToUpdate = prevLocations.find((location) => location.name === name)

      if (!locationToUpdate) return prevLocations

      const newDeltaQuantity = locationToUpdate.deltaQuantity! + 1
      const newReservedQuantity = (locationToUpdate.totalPickedQuantity ?? 0) - 1

      if (newDeltaQuantity !== 1 && pickedCards > 0) {
        setPickedCards((prevPickedCards) => prevPickedCards - 1)

        return prevLocations.map((location) =>
          location.name === name
            ? {
                ...location,
                deltaQuantity: newDeltaQuantity,
                reservedQuantity: newReservedQuantity,
              }
            : location
        )
      }

      return prevLocations
    })
  }

  const handleConfirmLocationsDialog = () => {
    const updated = new OrderArticle({
      ...orderArticle.toPlainObject(),
      locations: locationsObj.map((l) => ({ ...l })),
      locationInfo: orderArticle.locationInfo ? { ...orderArticle.locationInfo } : null,
    })
    setOpen(false)
    handleChoosePickingLocation(updated)
  }

  return (
    <ClosableDialog fullWidth={true} open={open} onClose={() => setOpen(false)}>
      {currentArticle ? (
        <>
          <h3>
            {!readonly && (
              <>
                {t('orders.picking.pickingDialogTitle')} {quantity}{' '}
                {t('orders.picking.pickingDialogTitlePtTwo')} |{' '}
              </>
            )}
            Order bin: {orderArticle.pickingId}
          </h3>
          <p>
            {t('orders.picking.pickingDialogSubtitle')} {pickedCards}{' '}
            {t('orders.picking.pickingDialogSubtitlePtTwo')}
          </p>
          <div className="container">
            <div className="row">
              <div className="col-12 col-md-6 d-flex justify-content-center">
                {currentArticle && (
                  <VisualArticle
                    displayOnly={true}
                    article={currentArticle}
                    visualArticleSizeClass="three"
                    currency={orderArticle.currencyCode as AppCurrency}
                  />
                )}
              </div>
              <div className="col-12 col-md-6 mt-3 mt-md-0">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th align="center">{readonly ? 'Picked location ' : 'Location'}</th>
                      {!readonly && <th align="center">Picked Qty</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {locationsObj
                      .filter((location) => {
                        if (readonly) return true
                        const initialLocation = initialLocations.find(
                          (loc) => loc.name === location.name
                        )
                        if (!initialLocation) return false
                        return (
                          showEmptyLocations ||
                          getAvailableQuantityNetOfPickedAndWithIssues(initialLocation) > 0
                        )
                      })
                      .map((location, index) => {
                        const initialLocation = initialLocations.find(
                          (loc) => loc.name === location.name
                        )
                        const initialQuantity = initialLocation
                          ? getAvailableQuantityNetOfPickedAndWithIssues(initialLocation)
                          : 0

                        return (
                          <tr key={index}>
                            <td align="center" data-testid={getDisplayLocationName(location.name)}>
                              {getDisplayLocationName(location.name)}:{' '}
                              {readonly ? location.deltaQuantity : initialQuantity}
                            </td>
                            {!readonly && (
                              <td align="center">
                                <div className="d-flex flex-row justify-content-center align-items-center">
                                  <i
                                    data-testid={`decrease-quantity-${getDisplayLocationName(
                                      location.name
                                    )}`}
                                    className="fa-light fa-circle-minus interactions-button"
                                    onClick={() => decrementPickingQuantity(location.name)}
                                  ></i>
                                  <Typography
                                    data-testid={`change-quantity-${getDisplayLocationName(
                                      location.name
                                    )}`}
                                    className="mx-2"
                                  >
                                    {Math.abs(location.deltaQuantity ?? 0)}
                                  </Typography>
                                  <i
                                    data-testid={`increase-quantity-${getDisplayLocationName(
                                      location.name
                                    )}`}
                                    className="fa-light fa-circle-plus interactions-button"
                                    onClick={() => incrementPickingQuantity(location.name)}
                                  ></i>
                                </div>
                              </td>
                            )}
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="w-100 mt-5">
            <div className="d-flex flex-row flex-wrap justify-content-end gap-2">
              <Button variant="outlined" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button variant="outlined" onClick={() => handleReportIssue(orderArticle)}>
                Report issue
              </Button>
              {!readonly && (
                <Button
                  variant="contained"
                  disabled={pickedCards !== quantity}
                  data-testid={'confirm-picked-locations'}
                  onClick={handleConfirmLocationsDialog}
                >
                  Confirm
                </Button>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <p className="mb-5">{t('orders.picking.pickingDialogErrorMessage')}</p>
          <div className="w-100 d-flex justify-content-center">
            <Button onClick={() => handleReportIssue(orderArticle)} variant="outlined">
              Report issue
            </Button>
          </div>
        </>
      )}
    </ClosableDialog>
  )
}
